import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { MessageSquare, Send, Rocket, CheckCircle2 } from "lucide-react";

export default function GrowthChannelsPanel() {
  const [state, setState] = useState(null);
  const [disc, setDisc] = useState("");
  const [tgTok, setTgTok] = useState("");
  const [tgChan, setTgChan] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/growth-channels");
    setState(data); setDisc(data.discord_webhook_url || ""); setTgChan(data.telegram_channel_id || "");
    setTgTok(data.telegram_bot_token || "");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/growth-channels", {
        discord_webhook_url: disc,
        telegram_bot_token: tgTok,
        telegram_channel_id: tgChan,
      });
      toast.success("Growth channels saved"); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try { await api.post("/admin/growth-channels/test"); toast.success("Test broadcast sent — check your channels"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!state) return null;

  return (
    <div className="border border-[#2A2A2A] bg-[#121212] p-6 mt-6">
      <div className="flex items-center gap-2 mb-4"><Rocket className="h-5 w-5 text-[#FF4500]" /><h3 className="font-display text-2xl">Growth channels</h3></div>
      <p className="font-mono text-xs text-gray-400 leading-relaxed mb-6">
        Every new track & album auto-broadcasts to your connected Discord + Telegram. Paste the URLs from the setup you did with the brand kit.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="h-4 w-4 text-[#FF4500]" />
            <span className="font-mono text-xs uppercase tracking-widest">Discord</span>
            {state.has_discord && <CheckCircle2 className="h-4 w-4 text-[#FF4500]" />}
          </div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Webhook URL</Label>
          <Input data-testid="gc-discord" value={disc} onChange={(e) => setDisc(e.target.value)}
                 placeholder="https://discord.com/api/webhooks/..."
                 className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono text-xs h-11" />
          <p className="font-mono text-[10px] text-gray-500 mt-2">Server Settings → Integrations → Webhooks → New Webhook → Copy URL</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Send className="h-4 w-4 text-[#FF4500]" />
            <span className="font-mono text-xs uppercase tracking-widest">Telegram</span>
            {state.has_telegram && <CheckCircle2 className="h-4 w-4 text-[#FF4500]" />}
          </div>
          <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Bot token</Label>
          <Input data-testid="gc-tg-tok" value={tgTok} onChange={(e) => setTgTok(e.target.value)}
                 placeholder="123456:ABC-DEF..."
                 className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono text-xs h-11" />
          <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-3 block">Channel ID</Label>
          <Input data-testid="gc-tg-chan" value={tgChan} onChange={(e) => setTgChan(e.target.value)}
                 placeholder="@melocraft_drops or -100..."
                 className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono text-xs h-11" />
          <p className="font-mono text-[10px] text-gray-500 mt-2">Message @BotFather → /newbot. Add the bot as admin to your channel.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <Button data-testid="gc-save" onClick={save} disabled={busy}
                className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-6">
          {busy ? "Saving..." : "Save channels"}
        </Button>
        <Button data-testid="gc-test" onClick={test} disabled={busy} variant="outline"
                className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-11 px-6">
          Send test broadcast
        </Button>
      </div>
    </div>
  );
}
